import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, errMsg, API_BASE } from '../lib/api';
import { getToken } from '../lib/auth';
import TemplateThumb from '../components/TemplateThumb';
import { Icon } from '../lib/icons';

interface TemplateItem {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  bgSvg?: string | null;
  layout?: string | null;
}

interface ThemeItem {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  config: Record<string, string>;
}

export default function CreateMemorial() {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState<TemplateItem[]>([]);
  const [themes, setThemes] = useState<ThemeItem[]>([]);

  const [form, setForm] = useState({
    deceasedName: '',
    deceasedNameEn: '',
    birthDate: '',
    deathDate: '',
    biography: '',
    epitaph: '',
    visibility: 'public',
    templateId: 'classic',
    themeId: 'ink',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [avatar, setAvatar] = useState<File | null>(null);
  const [avatarPreview, setAvatarPreview] = useState('');
  /** 合葬的第二位逝者；null = 单人馆 */
  const [second, setSecond] = useState<{
    name: string;
    gender: string;
    birthDate: string;
    deathDate: string;
  } | null>(null);

  useEffect(() => {
    api<{ items: TemplateItem[] }>('/templates').then((res) => {
      if (res.ok && res.data) setTemplates(res.data.items);
    });
    api<{ items: ThemeItem[] }>('/themes').then((res) => {
      if (res.ok && res.data) setThemes(res.data.items);
    });
  }, []);

  function set<K extends keyof typeof form>(k: K, v: string) {
    setForm((f) => ({ ...f, [k]: v }));
  }

  function setSecondField(k: 'name' | 'gender' | 'birthDate' | 'deathDate', v: string) {
    setSecond((s) => (s ? { ...s, [k]: v } : s));
  }

  /** 头像/遗像：建馆后上传到 R2，返回 object key（失败回 null，不影响建馆） */
  async function uploadPortrait(memorialId: string, file: File): Promise<string | null> {
    const pres = await api<{
      mode: 'presigned' | 'direct';
      key: string;
      uploadUrl?: string;
    }>('/media/portrait-presign', {
      method: 'POST',
      body: JSON.stringify({
        memorialId,
        filename: file.name,
        contentType: file.type,
        sizeBytes: file.size,
      }),
    });
    if (!pres.ok || !pres.data) return null;

    const { mode, key, uploadUrl } = pres.data;
    try {
      if (mode === 'presigned' && uploadUrl) {
        const up = await fetch(uploadUrl, {
          method: 'PUT',
          body: file,
          headers: { 'Content-Type': file.type },
        });
        if (!up.ok) return null;
      } else {
        const token = getToken();
        const up = await fetch(`${API_BASE}/media/upload?key=${encodeURIComponent(key)}`, {
          method: 'PUT',
          body: file,
          headers: {
            'Content-Type': file.type,
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        });
        if (!up.ok) return null;
      }
      return key;
    } catch {
      return null;
    }
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setLoading(true);

    // 主逝者沿用上方表单字段，第二位（合葬）拼在后面一并提交
    const res = await api<{ memorial: { id: string; slug: string } }>('/memorials', {
      method: 'POST',
      body: JSON.stringify({
        ...form,
        people: [
          {
            name: form.deceasedName,
            birthDate: form.birthDate,
            deathDate: form.deathDate,
          },
          ...(second && second.name.trim()
            ? [
                {
                  name: second.name.trim(),
                  gender: second.gender || null,
                  birthDate: second.birthDate || null,
                  deathDate: second.deathDate || null,
                },
              ]
            : []),
        ],
      }),
    });

    if (!res.ok || !res.data?.memorial) {
      setLoading(false);
      setError(errMsg(res, '创建失败'));
      return;
    }

    const { id, slug } = res.data.memorial;

    // 头像/遗像：建馆后上传到 R2，再回写 portraitKey
    if (avatar) {
      const key = await uploadPortrait(id, avatar);
      if (key) {
        await api(`/memorials/${id}`, {
          method: 'PATCH',
          body: JSON.stringify({ portraitKey: key }),
        });
      }
    }

    setLoading(false);
    navigate(`/m/${slug}`);
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-ink-100 to-ink-50 px-6 py-10">
      <div className="mx-auto max-w-xl">
        <h1 className="mb-2 font-serifcn text-2xl text-ink-900">创建纪念馆</h1>
        <p className="mb-8 text-sm text-ink-500">为先人建立一座可以随时探望的纪念馆</p>

        <form onSubmit={submit} className="rounded-2xl border border-ink-200 bg-white/80 p-6">
          <Input
            label="逝者姓名"
            required
            value={form.deceasedName}
            onChange={(v) => set('deceasedName', v)}
            placeholder="请输入姓名"
          />
          <Input
            label="英文名（可选）"
            value={form.deceasedNameEn}
            onChange={(v) => set('deceasedNameEn', v)}
            placeholder="海外亲属便于识别"
          />

          {/* 遗像（头像）上传 */}
          <div className="mb-6 flex items-center gap-4">
            <div className="relative h-24 w-24 shrink-0 overflow-hidden rounded-full border-2 border-ink-200 bg-ink-100">
              {avatarPreview ? (
                <img src={avatarPreview} alt="遗像预览" className="h-full w-full object-cover" />
              ) : (
                <div className="flex h-full items-center justify-center text-3xl text-ink-400"><Icon name="person" /></div>
              )}
            </div>
            <div>
              <p className="mb-1.5 text-xs text-ink-600">逝者遗像（头像）</p>
              <label className="cursor-pointer rounded-full bg-ink-900 px-4 py-2 text-xs text-ink-50">
                {avatar ? '重新选择' : '上传照片'}
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) {
                      setAvatar(f);
                      setAvatarPreview(URL.createObjectURL(f));
                    }
                  }}
                />
              </label>
              <p className="mt-1 text-[11px] text-ink-400">
                支持 jpg / png / webp / gif，将作为灵堂遗像展示
              </p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <Input
              label="出生日期"
              type="date"
              value={form.birthDate}
              onChange={(v) => set('birthDate', v)}
            />
            <Input
              label="忌日"
              type="date"
              value={form.deathDate}
              onChange={(v) => set('deathDate', v)}
            />
          </div>

          {/* 合葬：可添加第二位逝者 */}
          <div className="mb-6 rounded-xl border border-ink-200 bg-ink-50/40 p-4">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-xs font-medium text-ink-700">合葬（第二位逝者）</p>
                <p className="mt-0.5 text-[11px] leading-relaxed text-ink-500">
                  夫妻合葬等场景可添加，前台会并排显示两位逝者
                </p>
              </div>
              {second ? (
                <button
                  type="button"
                  onClick={() => setSecond(null)}
                  className="shrink-0 rounded-full border border-ink-300 px-3 py-1 text-[11px] text-ink-700 transition hover:bg-ink-100"
                >
                  移除
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() =>
                    setSecond({ name: '', gender: '', birthDate: '', deathDate: '' })
                  }
                  className="shrink-0 rounded-full bg-ink-900 px-3 py-1 text-[11px] text-ink-50 transition hover:bg-ink-800"
                >
                  + 添加
                </button>
              )}
            </div>

            {second && (
              <div className="mt-3">
                <Input
                  label="姓名"
                  required
                  value={second.name}
                  onChange={(v) => setSecondField('name', v)}
                  placeholder="第二位逝者姓名"
                />
                <div className="grid grid-cols-2 gap-4">
                  <Input
                    label="出生日期"
                    type="date"
                    value={second.birthDate}
                    onChange={(v) => setSecondField('birthDate', v)}
                  />
                  <Input
                    label="忌日"
                    type="date"
                    value={second.deathDate}
                    onChange={(v) => setSecondField('deathDate', v)}
                  />
                </div>
                <div>
                  <label className="mb-1.5 block text-xs text-ink-600">性别</label>
                  <select
                    value={second.gender}
                    onChange={(e) => setSecondField('gender', e.target.value)}
                    className="h-11 w-full rounded-xl border border-ink-200 bg-ink-50/50 px-3 text-sm outline-none focus:border-ink-500 focus:bg-white"
                  >
                    <option value="">未填写</option>
                    <option value="male">男</option>
                    <option value="female">女</option>
                  </select>
                </div>
              </div>
            )}
          </div>

          <div className="mb-4">
            <label className="mb-1.5 block text-xs text-ink-600">墓志铭</label>
            <input
              value={form.epitaph}
              onChange={(e) => set('epitaph', e.target.value)}
              placeholder="一句话概括他的一生"
              className="h-11 w-full rounded-xl border border-ink-200 bg-ink-50/50 px-3 text-sm outline-none focus:border-ink-500 focus:bg-white"
            />
          </div>

          <div className="mb-6">
            <label className="mb-1.5 block text-xs text-ink-600">生平简介</label>
            <textarea
              value={form.biography}
              onChange={(e) => set('biography', e.target.value)}
              rows={3}
              placeholder="记录他/她的故事…"
              className="w-full rounded-xl border border-ink-200 bg-ink-50/50 p-3 text-sm outline-none focus:border-ink-500 focus:bg-white"
            />
          </div>

          {/* 外观选择 */}
          <div className="mb-6 rounded-xl border border-ink-200 bg-ink-50/40 p-4">
            <p className="mb-3 text-xs font-medium text-ink-700">页面风格</p>

            <div className="mb-4 grid gap-2 sm:grid-cols-2">
              {templates.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => set('templateId', t.slug)}
                  className={`rounded-xl border p-3 text-left transition ${
                    form.templateId === t.slug
                      ? 'border-ink-900 bg-white'
                      : 'border-ink-200 bg-white/60 hover:border-ink-400'
                  }`}
                >
                  <TemplateThumb slug={t.slug} layout={t.layout} bgSvg={t.bgSvg} className="mb-2 text-ink-800" />
                  <p className="text-sm text-ink-900">{t.name}</p>
                  {t.description && (
                    <p className="mt-1 line-clamp-2 text-[11px] leading-relaxed text-ink-500">
                      {t.description}
                    </p>
                  )}
                </button>
              ))}
            </div>

            <p className="mb-2 text-xs font-medium text-ink-700">配色</p>
            <div className="flex flex-wrap gap-2">
              {themes.map((th) => (
                <button
                  key={th.id}
                  type="button"
                  onClick={() => set('themeId', th.slug)}
                  className={`flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs transition ${
                    form.themeId === th.slug
                      ? 'border-ink-900 bg-white'
                      : 'border-ink-200 bg-white/60'
                  }`}
                >
                  <span className="flex gap-1">
                    <span
                      className="h-3 w-3 rounded-full"
                      style={{ background: th.config['--c-bg'] }}
                    />
                    <span
                      className="h-3 w-3 rounded-full"
                      style={{ background: th.config['--c-accent'] }}
                    />
                    <span
                      className="h-3 w-3 rounded-full"
                      style={{ background: th.config['--c-text'] }}
                    />
                  </span>
                  {th.name}
                </button>
              ))}
            </div>
          </div>

          <div className="mb-6">
            <label className="mb-1.5 block text-xs text-ink-600">隐私设置</label>
            <div className="grid grid-cols-3 gap-2">
              {[
                { v: 'public', t: '公开' },
                { v: 'password', t: '密码访问' },
                { v: 'private', t: '仅家属' },
              ].map((o) => (
                <button
                  key={o.v}
                  type="button"
                  onClick={() => set('visibility', o.v)}
                  className={`h-10 rounded-xl border text-xs transition ${
                    form.visibility === o.v
                      ? 'border-ink-900 bg-ink-900 text-ink-50'
                      : 'border-ink-200 text-ink-600 hover:border-ink-400'
                  }`}
                >
                  {o.t}
                </button>
              ))}
            </div>
          </div>

          {error && (
            <p className="mb-4 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600">{error}</p>
          )}

          <button
            type="submit"
            disabled={loading}
            className="h-11 w-full rounded-xl bg-ink-900 text-sm text-ink-50 transition hover:bg-ink-800 disabled:opacity-50"
          >
            {loading ? '创建中…' : '创建纪念馆'}
          </button>
        </form>
      </div>
    </div>
  );
}

function Input({
  label,
  value,
  onChange,
  type = 'text',
  placeholder,
  required,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  placeholder?: string;
  required?: boolean;
}) {
  return (
    <div className="mb-4">
      <label className="mb-1.5 block text-xs text-ink-600">
        {label}
        {required && <span className="ml-1 text-red-500">*</span>}
      </label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        required={required}
        className="h-11 w-full rounded-xl border border-ink-200 bg-ink-50/50 px-3 text-sm outline-none focus:border-ink-500 focus:bg-white"
      />
    </div>
  );
}
